import { useRef, useState } from 'react';
import { Upload, X, AlertCircle } from 'lucide-react';
import { formatFileSize, fileTypeLabel } from '@/lib/fileFormat';

/**
 * A picked file together with the reason it was rejected, if any.
 *
 * The reason is stamped once, as the file enters the list, rather than derived
 * on render: that keeps it glued to its own row when a row above it is removed,
 * and runs `validate` once per file instead of once per file per render.
 */
export interface PickedFile {
  /** Stable key — an array index breaks the moment a middle row is removed. */
  id: string;
  file: File;
  /** Human-readable rejection reason, or null when the file is acceptable. */
  error: string | null;
}

interface FilePickerProps {
  files: PickedFile[];
  onFilesChange: (files: PickedFile[]) => void;
  /**
   * Returns a rejection reason, or null when the file passes. Rejected files stay
   * in the list and are flagged — the caller decides what to do about them, which
   * is usually to disable its submit button while any are present.
   */
  validate?: (file: File) => string | null;
  /** Native file-dialog hint only. `validate` is the real gate: drag & drop ignores accept. */
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  placeholder: string;
  /** Optional line under the placeholder, e.g. the accepted types and size limit. */
  hint?: string;
}

/** Same file picked twice is a no-op — matched on name + size + lastModified. */
function isSameFile(a: File, b: File): boolean {
  return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
}

export function FilePicker({
  files,
  onFilesChange,
  validate,
  accept,
  multiple = false,
  disabled = false,
  placeholder,
  hint,
}: FilePickerProps) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const addFiles = (list: FileList | null) => {
    if (!list || disabled) return;
    const incoming: PickedFile[] = Array.from(list)
      .filter((f) => !multiple || !files.some((p) => isSameFile(p.file, f)))
      .map((f) => ({
        id: crypto.randomUUID(),
        file: f,
        error: validate ? validate(f) : null,
      }));
    if (incoming.length === 0) return;
    onFilesChange(multiple ? [...files, ...incoming] : incoming.slice(-1));
  };

  const openPicker = () => {
    if (!disabled) inputRef.current?.click();
  };

  return (
    <div className="space-y-2">
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-5 text-center transition-colors select-none ${
          disabled
            ? 'cursor-not-allowed border-border/40 bg-muted/20 opacity-60'
            : dragging
              ? 'cursor-pointer border-primary bg-primary/10'
              : 'cursor-pointer border-border/60 bg-muted/30 hover:border-primary/50 hover:bg-muted/50'
        }`}
        onClick={openPicker}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPicker();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          addFiles(e.dataTransfer.files);
        }}
      >
        <Upload
          className={`mb-1.5 h-5 w-5 transition-colors ${dragging ? 'text-primary' : 'text-muted-foreground'}`}
        />
        <span className="text-xs font-medium text-muted-foreground">{placeholder}</span>
        <span className="mt-0.5 text-[11px] text-muted-foreground/70">or drag &amp; drop here</span>
        {hint && <span className="mt-1 text-[11px] text-muted-foreground/70">{hint}</span>}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          className="hidden"
          // Resetting the value lets the same file re-fire change after it was removed
          // from the list; without it a re-pick of that file looks like it was ignored.
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {files.length > 0 && (
        <ul className="max-h-56 space-y-1 overflow-y-auto pr-0.5">
          {files.map((p) => (
            <li
              key={p.id}
              className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 ${
                p.error
                  ? 'border-rose-300 bg-rose-50/70 dark:border-rose-900/60 dark:bg-rose-950/30'
                  : 'border-border/60 bg-muted/20'
              }`}
            >
              <span
                className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                  p.error
                    ? 'bg-rose-500/15 text-rose-700 dark:text-rose-400'
                    : 'bg-primary/10 text-primary'
                }`}
              >
                {fileTypeLabel(p.file)}
              </span>

              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium" title={p.file.name}>
                  {p.file.name}
                </p>
                {p.error ? (
                  <p className="flex items-center gap-1 text-[11px] text-rose-600 dark:text-rose-400">
                    <AlertCircle className="h-3 w-3 shrink-0" />
                    <span className="truncate">{p.error}</span>
                  </p>
                ) : (
                  <p className="text-[11px] text-muted-foreground">{formatFileSize(p.file.size)}</p>
                )}
              </div>

              <button
                type="button"
                disabled={disabled}
                aria-label={`Remove ${p.file.name}`}
                onClick={() => onFilesChange(files.filter((f) => f.id !== p.id))}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-rose-500/10 hover:text-rose-600 disabled:opacity-50 sm:h-8 sm:w-8"
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
